/**
 * Utility to parse selected nodes from various formats (v1 and v2) into
 * file IDs and folder selections that can be used with useFileSelection.
 */

import { ROOT_NODE_ID } from "@/constants";
import { FolderSelection } from "./features/selection";

export interface ParsedSelection {
  fileIds: string[];
  folderSelections: FolderSelection[];
}

/**
 * Parse selected nodes from file selection to extract file IDs and folder selections.
 * Handles both old v1 format (slash-joined strings with ROOT_NODE_ID) and new v2 format (typed objects).
 *
 * @param selectedNodes - Array of selected nodes in various formats
 * @returns Parsed file IDs and folder selections
 */
export function parseSelectedNodes(selectedNodes: any[]): ParsedSelection {
  const fileIds: string[] = [];
  const folderSelections: FolderSelection[] = [];

  if (!selectedNodes || !Array.isArray(selectedNodes)) {
    return { fileIds, folderSelections };
  }

  for (const node of selectedNodes) {
    if (!node) continue;

    if (typeof node === "string") {
      // String node - could be file ID, old slash-joined folder path, or JSON-stringified array
      if (node.startsWith(ROOT_NODE_ID + "/")) {
        // OLD FORMAT v1: "root_s971/Manager Data/LGP X" or "root_s971/drive_abc123/Manager Data"
        let pathPart = node.slice(ROOT_NODE_ID.length + 1);
        let driveId: string | null = null;
        if (pathPart.startsWith("drive_")) {
          const slashIndex = pathPart.indexOf("/");
          if (slashIndex !== -1) {
            driveId = pathPart.slice(6, slashIndex);
            pathPart = pathPart.slice(slashIndex + 1);
          } else {
            driveId = pathPart.slice(6);
            pathPart = "";
          }
        }
        if (pathPart) {
          folderSelections.push({ driveId, path: pathPart.split("/") });
        }
      } else if (node === ROOT_NODE_ID) {
        // Skip root node
        continue;
      } else if (node.startsWith("drive_")) {
        // Drive selection (possibly with folder path): "drive_xxx" or "drive_xxx/folder/path"
        const afterDrive = node.slice(6); // Remove "drive_" prefix
        const slashIndex = afterDrive.indexOf("/");
        if (slashIndex === -1) {
          // Just "drive_xxx" - entire drive selected
          folderSelections.push({ driveId: afterDrive, path: [] });
        } else {
          // "drive_xxx/folder/path" - specific folder in drive
          const driveId = afterDrive.slice(0, slashIndex);
          const pathPart = afterDrive.slice(slashIndex + 1);
          folderSelections.push({ driveId, path: pathPart.split("/") });
        }
      } else if (node.startsWith("[")) {
        // Skip JSON-stringified arrays (folder paths in new format stored as strings)
        // These should be parsed by the caller if needed
        continue;
      } else {
        // File ID string
        if (!node.includes(ROOT_NODE_ID)) {
          fileIds.push(node);
        }
      }
    } else if (typeof node === "object") {
      if (node.version === 2) {
        // NEW FORMAT v2
        if (node.isFile) {
          if (node.fileId) {
            fileIds.push(node.fileId);
          }
        } else {
          folderSelections.push({
            driveId: node.driveId || null,
            path: node.folderPath || [],
          });
        }
      } else if (node.isFile) {
        // Old v1 file: { id, isFile: true }
        if (node.id && !node.id.includes(ROOT_NODE_ID)) {
          fileIds.push(node.id);
        }
      } else if (node.id) {
        // Old v1 folder: { id: "root_s971/...", isFile: false } or { id: JSON-stringified path }
        if (node.id.startsWith(ROOT_NODE_ID + "/")) {
          let pathPart = node.id.slice(ROOT_NODE_ID.length + 1);
          let driveId: string | null = null;
          if (pathPart.startsWith("drive_")) {
            const slashIndex = pathPart.indexOf("/");
            if (slashIndex !== -1) {
              driveId = pathPart.slice(6, slashIndex);
              pathPart = pathPart.slice(slashIndex + 1);
            } else {
              driveId = pathPart.slice(6);
              pathPart = "";
            }
          }
          if (pathPart) {
            folderSelections.push({ driveId, path: pathPart.split("/") });
          }
        }
        // Skip unknown formats (not ROOT_NODE_ID path)
      }
    }
  }

  return { fileIds, folderSelections };
}

// Type definitions for v2 format
interface FolderNodeV2 {
  version: 2;
  driveId: string | null;
  folderPath: string[];
  isFile: false;
}

interface FileNodeV2 {
  version: 2;
  driveId: string | null;
  fileId: string;
  isFile: true;
}

export type SelectedNodeV2 = FolderNodeV2 | FileNodeV2;

/**
 * Build v2 format nodes for saving.
 * This is the inverse of parseSelectedNodes - it converts internal representation
 * back to the storage format.
 */
export function buildSelectedNodes(
  fileIds: string[],
  folderSelections: FolderSelection[]
): SelectedNodeV2[] {
  const nodes: SelectedNodeV2[] = [];
  for (const fileId of fileIds) {
    // Files: driveId is null since file IDs are globally unique
    nodes.push({ version: 2, driveId: null, fileId, isFile: true });
  }
  for (const folder of folderSelections) {
    nodes.push({
      version: 2,
      driveId: folder.driveId,
      folderPath: folder.path,
      isFile: false,
    });
  }
  return nodes;
}

/**
 * Convert FolderSelection[] to folderPaths (string[][]) for backward compatibility
 * with useFileSelection hook which uses folderPaths format.
 */
export function folderSelectionsToFolderPaths(
  folderSelections: FolderSelection[]
): string[][] {
  return folderSelections.map((fs) => {
    // In across-drives mode, prepend driveId to path
    if (fs.driveId) {
      return [fs.driveId, ...fs.path];
    }
    return fs.path;
  });
}
